import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useAppSelector } from '../../redux/hooks/redux';
import { selectCurrentCompany } from '../../redux/features/user/selectors/userState.selectors';
import { companyDayRange, countRealVisits } from './data/api';
import { App } from './App';
import { StoreProvider } from './state/store';
import './styles/app.css';

/** Zona del navegador; solo se usa si la empresa no tiene zona configurada (y se avisa). */
export function browserZone(): string | undefined {
  try {
    const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return z && DateTime.now().setZone(z).isValid ? z : undefined;
  } catch { return undefined; }
}

/** The imported interface keeps its own visual tokens inside this root. */
export function AgendaInteligenteRoute({ routeBase = '/business/agenda-inteligente' }: { routeBase?: string }) {
  const company = useAppSelector(selectCurrentCompany);
  const companyId = company?.id;
  const companyZone = company?.timeZone && DateTime.now().setZone(company.timeZone).isValid ? company.timeZone : undefined;
  // Muchas empresas aún no tienen zona horaria: se usa la de este equipo y se avisa (nunca UTC en silencio).
  const zone = companyZone ?? browserZone();
  const zoneNote = companyZone ? undefined : zone ? `Tu empresa no tiene zona horaria configurada; se usa la de este equipo (${zone}).` : undefined;
  const [readState, setReadState] = useState<{ companyId?: number; zone?: string; count?: number; error?: boolean }>({});
  const visible = readState.companyId === companyId && readState.zone === zone ? readState : {};
  useEffect(() => {
    let active = true;
    setReadState({ companyId, zone });
    if (!companyId || !zone) {
      setReadState({ companyId, zone, error: true });
      return () => { active = false; };
    }
    let startDate: string;
    let endDate: string;
    try {
      ({ startDate, endDate } = companyDayRange(DateTime.now(), zone));
    } catch {
      setReadState({ companyId, zone, error: true });
      return () => { active = false; };
    }
    countRealVisits(startDate, endDate)
      .then(value => { if (active) setReadState({ companyId, zone, count: value }); })
      .catch(() => { if (active) setReadState({ companyId, zone, error: true }); });
    return () => { active = false; };
  }, [companyId, zone]);
  return <div className="fn-inteligente">
    <div className="fn-preview-notice" role="status">
      Vista previa: el Calendario muestra tus visitas reales (solo lectura). Las pantallas que aún no usan tus datos aparecen como Próximamente.{' '}
      {visible.error ? 'No se pudieron consultar las visitas reales.' : visible.count === undefined ? 'Consultando visitas reales…' : `Visitas reales de hoy: ${visible.count}.`}
      {zoneNote ? ` ${zoneNote}` : ''}
    </div>
    <StoreProvider key={companyId ?? 'none'} routeBase={routeBase} zone={zone} zoneNote={zoneNote}><App /></StoreProvider>
  </div>;
}
